import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import Backdrop from "@mui/material/Backdrop";
import { useEffect, useRef, useState } from "react";
import z from "zod";
import { EmailModalItem } from "./EmailModalItem";

interface SendTestModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the confirmed recipient list. Returns a promise so the modal can show a
   * sending state and surface a failure inline instead of closing on a request that hasn't
   * resolved yet.
   */
  onSend?: (emails: string[]) => Promise<void>;
}

const isValidEmail = (value: string) => z.email().safeParse(value.trim()).success;

function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
}

export default function SendTestModal({ open, onClose, onSend }: SendTestModalProps) {
  const [inputValue, setInputValue] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [inputError, setInputError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setInputValue("");
      setEmails([]);
      setInputError("");
      setSending(false);
      setSendError("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && open) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  function handleAdd() {
    const parsed = parseEmails(inputValue);
    if (parsed.length === 0) return;

    const invalid = parsed.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      setInputError(`Invalid address${invalid.length > 1 ? "es" : ""}: ${invalid.join(", ")}`);
      return;
    }

    setInputError("");
    const fresh = parsed.filter((e) => !emails.includes(e));
    if (fresh.length > 0) setEmails((prev) => [...prev, ...fresh]);
    setInputValue("");
  }

  function handleRemove(email: string) {
    setEmails((prev) => prev.filter((e) => e !== email));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  }

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <Backdrop open onClick={onClose} sx={{ zIndex: 9500, background: "rgba(0,0,0,0.6)" }} />

      {/* Dialog panel — pointer-events-none on outer so backdrop button is clickable */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Send a Test Email"
        className="fixed inset-0 flex items-center justify-center pointer-events-none"
        style={{ zIndex: 9501 }}
      >
        <div
          className="bg-white flex flex-col pointer-events-auto"
          style={{
            width: 440,
            maxWidth: "90vw",
            borderRadius: 12,
            padding: 28,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          {/* Title */}
          <p
            className="font-crimson"
            style={{
              fontSize: 20,
              fontWeight: 400,
              color: "var(--color-rodeo-black)",
              marginBottom: 4,
            }}
          >
            Send a Test Email
          </p>

          {/* Description */}
          <p
            className="font-lato"
            style={{
              fontSize: 13,
              color: "var(--color-graphite)",
              lineHeight: 1.5,
              marginBottom: 20,
            }}
          >
            Add email addresses to receive a test version of this email. Clicking through from the
            test will trigger a full demo of the gift experience.
          </p>

          {/* Input row */}
          <div className="flex gap-2" style={{ marginBottom: inputError ? 4 : 0 }}>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                if (inputError) setInputError("");
              }}
              onKeyDown={handleKeyDown}
              placeholder="name@company.com, name2@company.com"
              className="font-lato placeholder:text-graphite"
              style={{
                flex: 1,
                padding: "8px 12px",
                border: `1px solid ${inputError ? "var(--color-good-wine)" : "var(--color-smoke)"}`,
                borderRadius: "var(--radius-sm)",
                fontSize: 14,
                outline: "none",
                transition: "border-color 0.15s",
              }}
              onFocus={(e) => {
                if (!inputError) e.currentTarget.style.borderColor = "var(--color-dark-dust)";
              }}
              onBlur={(e) => {
                if (!inputError) e.currentTarget.style.borderColor = "var(--color-smoke)";
              }}
            />
            <button
              type="button"
              onClick={handleAdd}
              className="font-lato"
              style={{
                padding: "8px 16px",
                border: "1px solid var(--color-dark-dust)",
                borderRadius: "var(--radius-sm)",
                background: "var(--color-white)",
                color: "var(--color-dark-dust)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-clear-dust)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-white)")}
            >
              Add
            </button>
          </div>

          {/* Validation error */}
          {inputError && (
            <p
              className="font-lato"
              style={{
                fontSize: 12,
                color: "var(--color-good-wine)",
                marginTop: 4,
                marginBottom: 8,
              }}
            >
              {inputError}
            </p>
          )}

          {/* Hint */}
          <p
            className="font-lato"
            style={{
              fontSize: 12,
              color: "var(--color-graphite)",
              marginTop: 8,
              marginBottom: 14,
              lineHeight: 1.4,
            }}
          >
            Tip: separate multiple emails with commas to add them all at once.
          </p>

          {/* Email list */}
          {emails.length > 0 ? (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                marginBottom: 20,
                maxHeight: 160,
                overflowY: "auto",
              }}
            >
              {emails.map((email) => (
                <EmailModalItem key={email} email={email} onRemove={() => handleRemove(email)} />
              ))}
            </ul>
          ) : (
            <p
              className="font-lato"
              style={{
                fontSize: 13,
                color: "var(--color-graphite)",
                fontStyle: "italic",
                marginBottom: 20,
              }}
            >
              No recipients added yet.
            </p>
          )}

          {/* Send error */}
          {sendError && (
            <p
              role="alert"
              className="font-lato"
              style={{
                fontSize: 12,
                color: "var(--color-good-wine)",
                marginTop: -8,
                marginBottom: 14,
              }}
            >
              {sendError}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="font-lato"
              style={{
                padding: "8px 18px",
                border: "1px solid var(--color-smoke)",
                borderRadius: "var(--radius-btn)",
                background: "var(--color-white)",
                color: "var(--color-graphite)",
                fontSize: 13,
                fontWeight: 600,
                cursor: sending ? "not-allowed" : "pointer",
                opacity: sending ? 0.5 : 1,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-rodeo-black)";
                e.currentTarget.style.color = "var(--color-rodeo-black)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-smoke)";
                e.currentTarget.style.color = "var(--color-graphite)";
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={emails.length === 0 || sending}
              onClick={async () => {
                setSendError("");
                setSending(true);
                try {
                  await onSend?.(emails);
                  onClose();
                } catch (e) {
                  setSendError(e instanceof Error ? e.message : "Couldn't send the test email.");
                } finally {
                  setSending(false);
                }
              }}
              className="font-lato inline-flex items-center gap-1.5"
              style={{
                padding: "8px 18px",
                border: "none",
                borderRadius: "var(--radius-btn)",
                background: "var(--color-dark-dust)",
                color: "var(--color-white)",
                fontSize: 13,
                fontWeight: 600,
                cursor: emails.length === 0 || sending ? "not-allowed" : "pointer",
                opacity: emails.length === 0 || sending ? 0.5 : 1,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                if (emails.length > 0 && !sending)
                  e.currentTarget.style.background = "var(--color-rodeo-black)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--color-dark-dust)";
              }}
            >
              <SendOutlinedIcon sx={{ fontSize: 16 }} />
              {sending ? "Sending…" : "Send Test"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
